import { Driver, DriverHook } from "driver.js";
import { defaultTourConfig } from ".";

// Export the tour steps so they can be reused in BrandGrandTour
export const brandProfileSetupTourSteps = [
  {
    element: "#settings",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        
          <div class="flex -space-x-2">
            <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              </div>
            </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-foreground">Welcome to Your Brand Setup!</span>
        </div>
      </div>`,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">Let's get your brand profile set up for success! This quick tour will guide you through completing your profile and company details.</p>
        <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
          <div class="flex items-start gap-2">
            <svg class="w-4 h-4 text-brand-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs text-brand-800 font-medium">Complete all sections to unlock the full potential of your affiliate program!</p>
          </div>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Estimated time: 3 minutes</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#my-details",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Personal Profile Setup</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Let's start with your personal information. This helps affiliates connect with you personally.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Make sure to fill in the following fields:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                </svg>
                Full name and email address
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Your current location (country)
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>This information is used to verify your identity.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#company-details",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Company Information</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Now let's set up your company details. This information will be visible to potential affiliates.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Company Details to Add:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                Company name and description
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                Company logo (recommended)
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
                Industry and business type
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>This helps affiliates understand your business and target audience.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#appearance-settings",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Appearance Settings</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Customize how your brand details page looks to potential affiliates. Create a professional and attractive presence.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Customize Your Brand Page:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
                </svg>
                Select theme styles and layouts
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
                </svg>
                Customize primary and secondary colors
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                Upload showcase images and products
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                Preview changes in real-time
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>A professional appearance builds trust with potential affiliates.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#program-settings",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Program Settings</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Configure your affiliate program settings to attract the right affiliates.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Program Configuration:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                Commission rates and structure
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
                Payment terms and methods
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
                Program requirements and guidelines
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Clear program settings help attract quality affiliates.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },

  {
    element: "#share-resources",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Share Resources</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Create and share helpful resources to empower your affiliates with the tools they need to succeed.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Resource Management:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Create resources with rich content
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
                Embed YouTube videos automatically
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                Edit and update resources anytime
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                Preview before sharing with affiliates
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Great resources help affiliates promote your brand effectively.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },

  {
    element: "#pricing",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
          </svg>
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Pricing & Payment</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-sm text-muted-foreground leading-relaxed">Set up your payment information to receive affiliate commissions and manage payouts.</p>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Payment Setup:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                Payment method setup
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
                Payout schedule configuration
              </div>
            </ol>
            <ol class="flex items-center gap-2">
              <div class="w-2 h-2 rounded-full"></div>
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
                Tax information
              </div>
            </ol>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Proper payment setup ensures smooth affiliate relationships.</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
];

const startProfileTour = (
  tourDriver: Driver,
  onNextClick: DriverHook,
  onPrevClick: DriverHook
) => {
  tourDriver.setConfig(defaultTourConfig);
  // Here we set the unique steps for the FIRST tour
  tourDriver.setSteps(
    brandProfileSetupTourSteps.map((step) => ({
      ...step,
      popover: {
        ...step.popover,
        onNextClick: (element, step, config) => {
          onNextClick(element, step, config);
          setTimeout(() => {
            tourDriver.moveNext();
          }, 200);
        },
        onPrevClick: (element, step, config) => {
          onPrevClick(element, step, config);
          setTimeout(() => {
            tourDriver.movePrevious();
          }, 200);
        },
      },
    }))
  );
  tourDriver.drive();
};

export default startProfileTour;
